import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import useCurrentUser from "@/hooks/useCurrentUser";
import useSpeechToText from "@/hooks/useSpeechToText";
import { dictationErrorMessage, TALK_IT_OUT_UNSUPPORTED_MESSAGE } from "@/lib/dictation";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { useAssumeUser } from "@/providers/AssumeUserProvider";
import { getImpersonationHeaders } from "@/providers/GraphqlProvider/headers";
import { getAgentChatUrl } from "./constants";
import McpPlaygroundChatView from "./McpPlaygroundChatView";
import { useMcpPlaygroundAuth } from "./useMcpPlaygroundAuth";
import { useMcpPlaygroundAgentChat } from "./useMcpPlaygroundAgentChat";

export default function McpPlaygroundChat() {
  const { getAccessToken } = useCurrentUser();
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;
  const { assumedUser } = useAssumeUser();
  const { status: authStatus } = useMcpPlaygroundAuth();
  const api = getAgentChatUrl();
  const [input, setInput] = useState("");

  const authedFetch = useCallback(
    async (input: RequestInfo | URL, init?: RequestInit) => {
      const token = await getAccessTokenRef.current();
      const headers = new Headers(init?.headers);
      headers.set("Authorization", `Bearer ${token}`);

      Object.entries(getImpersonationHeaders(assumedUser?.id)).forEach(([key, value]) => {
        headers.set(key, value);
      });

      return fetch(input as RequestInfo, {
        ...init,
        headers
      });
    },
    [assumedUser?.id]
  );

  const { messages, sendMessage, isLoading, error } = useMcpPlaygroundAgentChat({
    api,
    fetch: authedFetch as typeof fetch
  });

  const { listening, toggle, stop } = useSpeechToText({
    value: input,
    onChange: setInput,
    onUnsupported: () => showSnackbar(TALK_IT_OUT_UNSUPPORTED_MESSAGE),
    onError: (error) => {
      const message = dictationErrorMessage(error);
      if (message) showErrorSnackbar(message);
    }
  });

  useEffect(() => {
    if (isLoading) {
      stop();
    }
  }, [isLoading, stop]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const text = input.trim();
      if (!text || isLoading) {
        return;
      }

      stop();
      setInput("");
      void sendMessage(text);
    },
    [input, isLoading, sendMessage, stop]
  );

  const handleSelectRecord = useCallback(
    (value: string) => {
      void sendMessage(value);
    },
    [sendMessage]
  );

  return (
    <McpPlaygroundChatView
      messages={messages}
      input={input}
      isLoading={isLoading}
      error={error}
      authStatus={authStatus}
      listening={listening}
      onInputChange={setInput}
      onSubmit={handleSubmit}
      onSelectRecord={handleSelectRecord}
      onToggleDictation={toggle}
    />
  );
}
